import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Monitor, RefreshCw, Search, ChevronDown, ShieldOff } from "lucide-react";
import { apiGet, apiPost } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./admin-shell.css";
import "./LicenseManager.css";

/* ── helpers ── */
function normalizeText(v) { return String(v || "").trim(); }

function fmt(v) {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return "-";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(t));
}

function shortFp(v) {
  const s = normalizeText(v);
  return s ? s.slice(0, 18).toUpperCase() : "UNBOUND";
}

export default function LicenseManager() {
  /* ── state ── */
  const [tenants,           setTenants]           = useState([]);
  const [selectedTenantId,  setSelectedTenantId]  = useState("");
  const [agents,            setAgents]            = useState([]);
  const [tenantSearch,      setTenantSearch]      = useState("");
  const [agentSearch,       setAgentSearch]       = useState("");
  const [agentStatus,       setAgentStatus]       = useState("all");
  const [seatInput,         setSeatInput]         = useState(5);
  const [loadingTenants,    setLoadingTenants]    = useState(true);
  const [loadingAgents,     setLoadingAgents]     = useState(false);
  const [savingSeats,       setSavingSeats]       = useState(false);
  const [tenantDropOpen,    setTenantDropOpen]    = useState(false);
  const [blockedAgents,     setBlockedAgents]     = useState([]);

  /* ── fetch ── */
  const fetchTenants = async () => {
    setLoadingTenants(true);
    try {
      const res  = await apiGet("/api/admin/tenants");
      const data = await res.json().catch(() => []);
      if (!res.ok) { toast.error("Failed to fetch tenants"); return; }
      const list = Array.isArray(data) ? data : [];
      setTenants(list);
      setSelectedTenantId(cur => (cur && list.some(t => t.id === cur) ? cur : list[0]?.id || ""));
    } catch { toast.error("Failed to fetch tenants"); }
    finally { setLoadingTenants(false); }
  };

  const fetchAgents = async (tid) => {
    if (!tid) { setAgents([]); return; }
    setLoadingAgents(true);
    try {
      const res  = await apiGet(`/api/admin/tenants/${encodeURIComponent(tid)}/agents`);
      const data = await res.json().catch(() => []);
      if (!res.ok) { toast.error("Failed to fetch agents"); return; }
      setAgents(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to fetch agents"); }
    finally { setLoadingAgents(false); }
  };

  const fetchBlockedAgents = async (tid) => {
    if (!tid) { setBlockedAgents([]); return; }
    try {
      const res  = await apiGet(`/api/admin/tenants/${encodeURIComponent(tid)}/blocked-agents`);
      const data = await res.json().catch(() => []);
      if (!res.ok) return;
      setBlockedAgents(Array.isArray(data) ? data : []);
    } catch { /* non-critical, don't toast */ }
  };

  useEffect(() => { void fetchTenants(); }, []);

  useEffect(() => {
    const t = tenants.find(t => t.id === selectedTenantId);
    setSeatInput(t?.maxSeats ?? 5);
    void fetchAgents(selectedTenantId);
    void fetchBlockedAgents(selectedTenantId);
  }, [selectedTenantId, tenants]);

  /* ── derived ── */
  const selectedTenant = useMemo(() => tenants.find(t => t.id === selectedTenantId) || null, [selectedTenantId, tenants]);

  const filteredTenants = useMemo(() => {
    const q = normalizeText(tenantSearch).toLowerCase();
    if (!q) return tenants;
    return tenants.filter(t =>
      [t?.name, t?.ownerEmail].some(v => normalizeText(v).toLowerCase().includes(q))
    );
  }, [tenants, tenantSearch]);

  const filteredAgents = useMemo(() => {
    const q = normalizeText(agentSearch).toLowerCase();
    return agents.filter(a => {
      const mq = !q || [a?.agentId, a?.hostname, a?.name, a?.ip, a?.fingerprint]
        .some(v => normalizeText(v).toLowerCase().includes(q));
      const ms = agentStatus === "all"
        || (agentStatus === "licensed" &&  a?.isLicensed)
        || (agentStatus === "pending"  && !a?.isLicensed);
      return mq && ms;
    });
  }, [agents, agentSearch, agentStatus]);

  const licensed = useMemo(() => agents.filter(a => a?.isLicensed).length, [agents]);
  const pending  = Math.max(0, agents.length - licensed);

  /* ── actions ── */
  const saveSeats = async () => {
    if (!selectedTenant || savingSeats) return;
    const n = Math.max(1, Number.parseInt(String(seatInput), 10) || 1);
    setSavingSeats(true);
    try {
      const res = await apiPost(`/api/admin/tenants/${encodeURIComponent(selectedTenant.id)}/seats`, { maxSeats: n });
      if (!res.ok) { toast.error("Could not update seat limit"); return; }
      toast.success("Seat allocation updated");
      setTenants(ts => ts.map(t => t.id === selectedTenant.id ? { ...t, maxSeats: n } : t));
    } catch { toast.error("Could not update seat limit"); }
    finally { setSavingSeats(false); }
  };

  const approve = async (id) => {
    const res  = await apiPost(`/api/admin/agents/${encodeURIComponent(id)}/approve`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Approval failed"); return; }
    toast.success("License approved");
    void fetchAgents(selectedTenantId);
  };

  const blockAgent = async (agent) => {
    if (!window.confirm(
      `Block "${agent.hostname || agent.agentId}"?\n\n` +
      `• The device will receive an uninstall command\n` +
      `• Its seat will be freed for a new device\n` +
      `• All telemetry data is preserved in the Blocked list\n` +
      `• You can unblock it at any time`
    )) return;
    const res  = await apiPost(`/api/admin/agents/${encodeURIComponent(agent._id)}/block`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Block failed"); return; }
    toast.success(data?.message || "Agent blocked");
    void fetchAgents(selectedTenantId);
    void fetchBlockedAgents(selectedTenantId);
    void fetchTenants();
  };

  const unblockAgent = async (agent) => {
    if (!window.confirm(
      `Unblock "${agent.hostname || agent.agentId}"?\n\n` +
      `The device will be allowed to re-register. A new license approval will be required.`
    )) return;
    const res  = await apiPost(`/api/admin/agents/${encodeURIComponent(agent._id)}/unblock`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data?.message || "Unblock failed"); return; }
    toast.success(data?.message || "Agent unblocked");
    void fetchBlockedAgents(selectedTenantId);
    void fetchTenants();
  };

  /* ── render ── */
  return (
    <div className="admin-workspace">

      {/* ── Page header ── */}
      <div className="admin-shell-header">
        <div className="admin-shell-copy">
          <span className="admin-shell-eyebrow">License Manager</span>
          <h1 className="admin-shell-title">Endpoint Licensing</h1>
          <p className="admin-shell-subtitle">
            Select a tenant workspace to view and manage endpoint licenses. Block a device to free its seat and send an uninstall command — all data is preserved and the device can be unblocked at any time.
          </p>
        </div>
        <div className="admin-shell-actions">
          <button className="gc-icon-btn" data-tip="Refresh" onClick={() => { void fetchTenants(); void fetchAgents(selectedTenantId); void fetchBlockedAgents(selectedTenantId); }}>
          <RefreshCw size={14} />
        </button>
        </div>
      </div>

      {/* ════════════════════════════════════════
          Control bar — one row, everything visible
          ════════════════════════════════════════ */}
      <div className="lm-control-bar">

        {/* Tenant picker */}
        <div className="lm-tenant-picker">
          <span className="lm-picker-label">Workspace</span>
          <div className="lm-picker-wrap" style={{ position: "relative" }}>
            <button
              type="button"
              className="lm-picker-btn"
              onClick={() => setTenantDropOpen(v => !v)}
              disabled={loadingTenants}
            >
              <Monitor size={15} />
              <span className="lm-picker-name">
                {loadingTenants ? "Loading…" : (selectedTenant?.name || "Select tenant")}
              </span>
              <ChevronDown size={13} style={{ marginLeft: "auto", flexShrink: 0 }} />
            </button>

            {tenantDropOpen && (
              <>
                <div
                  style={{ position: "fixed", inset: 0, zIndex: 199 }}
                  onClick={() => { setTenantDropOpen(false); setTenantSearch(""); }}
                />
              <div className="lm-picker-drop">
                <div className="lm-picker-search">
                  <Search size={13} />
                  <input
                    autoFocus
                    type="search"
                    placeholder="Filter tenants…"
                    value={tenantSearch}
                    onChange={e => setTenantSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <ul className="lm-picker-list">
                  {filteredTenants.length === 0
                    ? <li className="lm-picker-empty">No tenants found</li>
                    : filteredTenants.map(t => (
                      <li
                        key={t.id}
                        className={`lm-picker-option${selectedTenantId === t.id ? " is-active" : ""}`}
                        onClick={() => { setSelectedTenantId(t.id); setTenantDropOpen(false); setTenantSearch(""); }}
                      >
                        <div className="lm-picker-option-name">{t.name || "Unnamed"}</div>
                        <div className="lm-picker-option-email">{t.ownerEmail || "-"}</div>
                      </li>
                    ))
                  }
                </ul>
              </div>
              </>
            )}
          </div>
        </div>

        {/* Seat cap */}
        {selectedTenant && (
          <div className="lm-seat-block">
            <span className="lm-picker-label">Seat Cap</span>
            <div className="lm-seat-row">
              <input
                type="number"
                min="1"
                className="admin-input lm-seat-input"
                value={seatInput}
                onChange={e => setSeatInput(Math.max(1, Number.parseInt(e.target.value || "1", 10) || 1))}
              />
              <button className="admin-btn" onClick={saveSeats} disabled={savingSeats}>
                {savingSeats ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        {selectedTenant && (
          <div className="lm-stats">
            <div className="lm-stat">
              <span>Licensed</span>
              <strong style={{ color: "var(--as-success)" }}>{licensed}</strong>
            </div>
            <div className="lm-stat lm-stat--div">/</div>
            <div className="lm-stat">
              <span>Cap</span>
              <strong>{selectedTenant.maxSeats === null ? "∞" : Number(selectedTenant.maxSeats || 5)}</strong>
            </div>
            <div className="lm-stat lm-stat--div">·</div>
            <div className="lm-stat">
              <span>Registered</span>
              <strong>{agents.length}</strong>
            </div>
            <div className="lm-stat lm-stat--div">·</div>
            <div className="lm-stat">
              <span>Pending</span>
              <strong style={{ color: pending > 0 ? "var(--as-warning)" : undefined }}>{pending}</strong>
            </div>
          </div>
        )}
      </div>

      {/* ── Agent filters (second row) ── */}
      {selectedTenant && (
        <div className="admin-toolbar">
          <label className="admin-search" style={{ flex: "1 1 380px" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search agent ID, hostname, IP address, or fingerprint…"
              value={agentSearch}
              onChange={e => setAgentSearch(e.target.value)}
            />
          </label>
          <select
            className="admin-filter"
            value={agentStatus}
            onChange={e => setAgentStatus(e.target.value)}
          >
            <option value="all">All endpoints</option>
            <option value="licensed">Licensed only</option>
            <option value="pending">Pending only</option>
          </select>
          <span className="admin-panel-kicker">{filteredAgents.length} of {agents.length} endpoints</span>
        </div>
      )}

      {/* ── Agents table ── */}
      {!selectedTenant ? (
        <div className="admin-empty-state" style={{ marginTop: 0 }}>
          Select a tenant workspace above to view and manage endpoint licenses.
        </div>
      ) : loadingAgents ? (
        <div className="admin-empty-state">Loading endpoints…</div>
      ) : filteredAgents.length === 0 ? (
        <div className="admin-empty-state">
          {agents.length === 0
            ? "No endpoints registered for this tenant yet."
            : "No endpoints match the current search / filter."}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table lm-table">
            <thead>
              <tr>
                <th>Agent ID</th>
                <th>Hostname</th>
                <th>IP Address</th>
                <th>Fingerprint</th>
                <th>OS / Platform</th>
                <th>Status</th>
                <th>Last Sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map(a => (
                <tr key={a._id}>
                  <td>
                    <div className="admin-cell-title admin-monospace" style={{ fontSize: "0.82rem" }}>
                      {a.agentId || "-"}
                    </div>
                  </td>
                  <td>
                    <div className="admin-cell-title">{a.hostname || a.name || "-"}</div>
                  </td>
                  <td>
                    <span style={{ fontFamily: "Consolas, monospace", fontSize: "0.82rem", color: "var(--as-text-2)" }}>
                      {a.ip || "-"}
                    </span>
                  </td>
                  <td>
                    <span className="admin-monospace" style={{ fontSize: "0.75rem", color: "var(--as-text-2)" }}>
                      {shortFp(a.fingerprint)}
                    </span>
                  </td>
                  <td>
                    <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>
                      {a.platform || a.os || "-"}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-status ${a.isLicensed ? "is-provisioned" : "is-pending"}`}>
                      {a.isLicensed ? "Licensed" : "Pending"}
                    </span>
                  </td>
                  <td>
                    <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{fmt(a.lastSeen)}</div>
                  </td>
                  <td>
                    <div className="admin-actions">
                      {!a.isLicensed && (
                        <button
                          className="admin-inline-action"
                          style={{ color: "var(--as-success)", borderColor: "rgba(16,124,16,.3)" }}
                          onClick={() => approve(a._id)}
                          title="Approve license — assigns a seat to this device"
                        >
                          <CheckCircle2 size={13} /> Approve
                        </button>
                      )}
                      <button
                        className="admin-inline-action"
                        style={{ color: "var(--as-danger)", borderColor: "rgba(164,38,44,.25)" }}
                        onClick={() => blockAgent(a)}
                        title="Block — device uninstalls itself, seat is freed, data preserved"
                      >
                        <ShieldOff size={13} /> Block
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Blocked Devices ── */}
      {selectedTenantId && (
        <div className="admin-table-wrap" style={{ marginTop: 28 }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--as-border)", display: "flex", alignItems: "center", gap: 10 }}>
            <ShieldOff size={15} style={{ color: "var(--as-danger)" }} />
            <div>
              <span style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--as-danger)" }}>
                Blocked Devices
              </span>
              <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "var(--as-muted)" }}>
                {blockedAgents.length === 0
                  ? "No blocked devices — all endpoints are active"
                  : `${blockedAgents.length} device${blockedAgents.length !== 1 ? "s" : ""} blocked · seats freed · data preserved`}
              </span>
            </div>
          </div>
          {blockedAgents.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: "20px 16px" }}>
              No blocked devices for this tenant.
            </div>
          ) : (
            <table className="admin-table lm-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Hostname</th>
                  <th>IP Address</th>
                  <th>OS / Platform</th>
                  <th>Last Seen</th>
                  <th>Blocked On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blockedAgents.map(a => (
                  <tr key={a._id}>
                    <td>
                      <div className="admin-cell-title admin-monospace" style={{ fontSize: "0.82rem" }}>
                        {a.agentId || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="admin-cell-title">{a.hostname || a.name || "-"}</div>
                    </td>
                    <td>
                      <span style={{ fontFamily: "Consolas, monospace", fontSize: "0.82rem", color: "var(--as-text-2)" }}>
                        {a.ip || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>
                        {a.platform || a.os || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{fmt(a.lastSeen)}</div>
                    </td>
                    <td>
                      <div className="admin-cell-subtitle" style={{ marginTop: 0, color: "var(--as-danger)" }}>
                        {fmt(a.blockedAt)}
                      </div>
                    </td>
                    <td>
                      <div className="admin-actions">
                        <button
                          className="admin-inline-action"
                          style={{ color: "var(--as-success)", borderColor: "rgba(16,124,16,.3)" }}
                          onClick={() => unblockAgent(a)}
                          title="Unblock — allow this device to re-register"
                        >
                          <CheckCircle2 size={13} /> Unblock
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Scoped styles */}
      <style>{`
        /* ── Control bar ── */
        .lm-control-bar {
          display: flex;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 16px;
          padding: 14px 16px;
          margin-bottom: 12px;
          background: var(--as-surface);
          border: 1px solid var(--as-border);
          border-radius: 4px;
          box-shadow: var(--as-shadow);
        }

        .lm-picker-label {
          display: block;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--as-text-3);
          margin-bottom: 5px;
        }

        /* Tenant picker */
        .lm-tenant-picker { flex: 0 0 260px; }

        .lm-picker-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          height: 32px;
          padding: 0 10px;
          border: 1px solid var(--as-border-2);
          border-radius: 4px;
          background: var(--as-surface);
          color: var(--as-text);
          font-size: 0.875rem;
          font-family: inherit;
          cursor: pointer;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .lm-picker-btn:hover { border-color: var(--as-primary); }
        .lm-picker-btn:disabled { opacity: .6; cursor: default; }

        .lm-picker-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: left;
        }

        .lm-picker-drop {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          z-index: 200;
          width: 320px;
          background: var(--as-surface);
          border: 1px solid var(--as-border-2);
          border-radius: 4px;
          box-shadow: 0 4px 16px rgba(0,0,0,.14);
          overflow: hidden;
        }

        .lm-picker-search {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border-bottom: 1px solid var(--as-border);
          color: var(--as-text-3);
        }
        .lm-picker-search input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          color: var(--as-text);
          font: inherit;
          font-size: 0.875rem;
        }

        .lm-picker-list {
          list-style: none;
          margin: 0;
          padding: 4px 0;
          max-height: 280px;
          overflow-y: auto;
        }
        .lm-picker-empty { padding: 12px 14px; color: var(--as-text-3); font-size: 0.875rem; }

        .lm-picker-option {
          padding: 8px 14px;
          cursor: pointer;
          transition: background .12s ease;
        }
        .lm-picker-option:hover { background: var(--as-primary-bg); }
        .lm-picker-option.is-active { background: var(--as-primary-bg); border-left: 2px solid var(--as-primary); }
        .lm-picker-option-name { font-size: 0.875rem; font-weight: 600; color: var(--as-text); }
        .lm-picker-option-email { font-size: 0.75rem; color: var(--as-text-3); margin-top: 2px; }

        /* Seat block */
        .lm-seat-block { flex: 0 0 auto; }
        .lm-seat-row { display: flex; align-items: center; gap: 6px; }
        .lm-seat-input { width: 80px !important; min-height: 32px !important; }

        /* Stats */
        .lm-stats {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: auto;
          flex-wrap: wrap;
        }
        .lm-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
          min-width: 50px;
        }
        .lm-stat span {
          font-size: 0.67rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--as-text-3);
        }
        .lm-stat strong {
          font-size: 1rem;
          font-weight: 700;
          color: var(--as-text);
        }
        .lm-stat--div {
          color: var(--as-text-3);
          font-size: 1.1rem;
          min-width: auto;
          padding-top: 12px;
        }

        /* Table */
        .lm-table { min-width: 1000px; }
      `}</style>
    </div>
  );
}
